const OPENPAY_FORM_ID = 'openpay-card-form';

export const initializeOpenPay = (
  merchantId,
  publicKey,
  sandboxMode = true
) => {
  const { OpenPay } = window;

  if (!OpenPay) {
    throw new Error('OpenPay SDK not available');
  }

  OpenPay.setId(merchantId);
  OpenPay.setApiKey(publicKey);
  OpenPay.setSandboxMode(sandboxMode);
};

export const getFormFields = () => {
  const formElement = document.getElementById(OPENPAY_FORM_ID);
  if (!formElement) {
    return null;
  }

  return {
    holderName: formElement.querySelector('[data-openpay-card="holder_name"]'),
    cardNumber: formElement.querySelector('[data-openpay-card="card_number"]'),
    expirationMonth: formElement.querySelector(
      '[data-openpay-card="expiration_month"]'
    ),
    expirationYear: formElement.querySelector(
      '[data-openpay-card="expiration_year"]'
    ),
    cvv: formElement.querySelector('[data-openpay-card="cvv2"]')
  };
};

export const validateFormFields = fields => {
  if (!fields.holderName?.value.trim()) {
    return 'Por favor ingresa el nombre del titular de la tarjeta';
  }

  if (!fields.cardNumber?.value.trim()) {
    return 'El número de tarjeta es requerido';
  }

  const cardNumber = fields.cardNumber.value.replace(/\s/g, '');
  if (cardNumber.length < 13 || cardNumber.length > 19) {
    return 'El número de tarjeta debe tener entre 13 y 19 dígitos';
  }

  if (!fields.expirationMonth?.value.trim()) {
    return 'El mes de expiración es requerido';
  }

  const monthValue = fields.expirationMonth.value.trim();
  const month = parseInt(monthValue, 10);
  if (Number.isNaN(month) || month < 1 || month > 12) {
    return 'El mes de expiración debe ser un número entre 01 y 12';
  }

  if (!fields.expirationYear?.value.trim()) {
    return 'El año de expiración es requerido';
  }

  const year = fields.expirationYear.value.trim();
  if (year.length !== 4) {
    return 'El año de expiración debe tener 4 dígitos (ej: 2025)';
  }

  const yearNum = parseInt(year, 10);
  const currentYear = new Date().getFullYear();
  if (Number.isNaN(yearNum) || yearNum < currentYear) {
    return 'El año de expiración debe ser un año futuro válido';
  }

  if (!fields.cvv?.value.trim()) {
    return 'El CVV es requerido';
  }

  const cvv = fields.cvv.value;
  if (cvv.length < 3 || cvv.length > 4) {
    return 'El CVV debe tener 3 o 4 dígitos';
  }

  return null;
};

export const formatCardData = fields => {
  const cardNumber = fields.cardNumber.value.replace(/\D/g, '');
  const month = fields.expirationMonth.value.trim().padStart(2, '0');
  const year = fields.expirationYear.value.trim();
  const yearFormatted = year.length === 4 ? year.substring(2) : year;
  const holderName = fields.holderName.value.trim();
  const cvv = fields.cvv.value.trim();

  return {
    holder_name: holderName,
    card_number: cardNumber,
    expiration_month: month,
    expiration_year: yearFormatted,
    cvv2: cvv
  };
};

export const validateWithOpenPay = (cardData, OpenPay) => {
  if (OpenPay.card?.validateCardNumber) {
    const isValidCardNumber = OpenPay.card.validateCardNumber(
      cardData.card_number
    );
    if (!isValidCardNumber) {
      return 'El número de tarjeta no es válido. Verifica que sea un número de tarjeta válido.';
    }
  }

  if (OpenPay.card?.validateCVC) {
    const isValidCVC = OpenPay.card.validateCVC(
      cardData.cvv2,
      cardData.card_number
    );
    if (!isValidCVC) {
      return 'El CVV no es válido para este tipo de tarjeta.';
    }
  }

  if (OpenPay.card?.validateExpiry) {
    const isValidExpiry = OpenPay.card.validateExpiry(
      cardData.expiration_month,
      cardData.expiration_year
    );
    if (!isValidExpiry) {
      return 'La fecha de expiración no es válida o ya expiró.';
    }
  }

  return null;
};

export const getDeviceSessionId = () => {
  const { OpenPay } = window;

  if (!OpenPay || !OpenPay.deviceData) {
    throw new Error('OpenPay deviceData is not available');
  }

  const deviceSessionId = OpenPay.deviceData.setup();

  return deviceSessionId;
};

export const extractTokenFromResponse = response => {
  try {
    if (response?.data?.id) {
      return String(response.data.id);
    }
  } catch (e) {
    return null;
  }
  return null;
};

export const extractErrorMessage = (response, t) => {
  const defaultMessage = t('OPENPAY_CARD_FORM.ERRORS.TOKEN_GENERATION_FAILED');

  if (!response) {
    return defaultMessage;
  }

  const description =
    response.data?.description ||
    response.data?.message ||
    response.message ||
    response.description ||
    (typeof response.data === 'string' ? response.data : null) ||
    (typeof response === 'string' ? response : null);

  if (!description) {
    return defaultMessage;
  }

  const errorCode = response.data?.error_code;
  if (errorCode) {
    return `${description} (Código: ${errorCode})`;
  }

  return description;
};

export const formatSyncErrorMessage = syncError => {
  const errorMessage =
    typeof syncError === 'string'
      ? syncError
      : syncError?.message || syncError?.toString() || 'Error desconocido';

  if (
    errorMessage.toLowerCase().includes('invalid') ||
    errorMessage.toLowerCase().includes('inválid')
  ) {
    return `La tarjeta fue rechazada por OpenPay: ${errorMessage}. Asegúrate de usar una tarjeta de prueba válida de OpenPay sandbox. Ejemplos: Visa: 4111111111111111, Mastercard: 5105105105105100`;
  }

  return `Error al procesar la tarjeta: ${errorMessage}`;
};
